import { createContext, useContext, useEffect, useMemo, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { authApi } from '@/api/auth';
import { auth } from '@/shared/auth/storage';
import { setApiActiveScope } from '@/shared/api/client';
import { buildScopedPath, isLikelyGuid, publicRoutes, type RouteScope, type ScopedRouteKey } from '@/app/routes';

interface ScopeValue {
  tenantId: string;
  companyId: string;
  tenantName: string;
  companyName: string;
  status: 'pending' | 'ok' | 'forbidden';
  switchTo: (next: RouteScope, route?: ScopedRouteKey) => void;
}

const Ctx = createContext<ScopeValue | null>(null);

export function useTenantScope() {
  const c = useContext(Ctx);
  if (!c) throw new Error('useTenantScope must be used within TenantScopeProvider');
  return c;
}

export function useScopedNavigate() {
  const scope = useTenantScope();
  const nav = useNavigate();
  return (routeKey: ScopedRouteKey, query?: Record<string, string | number | null | undefined>) =>
    nav(buildScopedPath(routeKey, { tenantId: scope.tenantId, companyId: scope.companyId }, query));
}

export function TenantScopeProvider({ children }: { children: ReactNode }) {
  const { tenantId = '', companyId = '' } = useParams();
  const nav = useNavigate();
  const validGuids = isLikelyGuid(tenantId) && isLikelyGuid(companyId);

  const tenantsQ = useQuery({
    queryKey: ['scope', 'tenants'],
    queryFn: authApi.myTenants,
    enabled: validGuids,
  });
  const companiesQ = useQuery({
    queryKey: ['scope', 'companies', tenantId],
    queryFn: () => authApi.companiesByTenant(tenantId),
    enabled: validGuids && !!tenantsQ.data,
  });

  const tenant = tenantsQ.data?.find((t) => t.Id === tenantId);
  const company = companiesQ.data?.find((c) => c.Id === companyId);

  const status: ScopeValue['status'] = !validGuids
    ? 'forbidden'
    : tenantsQ.isLoading || companiesQ.isLoading
      ? 'pending'
      : tenant && company
        ? 'ok'
        : 'forbidden';

  useEffect(() => {
    if (status === 'ok') {
      setApiActiveScope({ tenantId, companyId });
      auth.setScopeCache({
        tenantId,
        companyId,
        tenantName: tenant?.Name,
        companyName: company?.Name,
      });
    }
  }, [status, tenantId, companyId, tenant?.Name, company?.Name]);

  useEffect(() => {
    if (status === 'forbidden') nav(publicRoutes.selectScope, { replace: true });
  }, [status, nav]);

  const value = useMemo<ScopeValue>(
    () => ({
      tenantId,
      companyId,
      tenantName: tenant?.Name ?? '',
      companyName: company?.Name ?? '',
      status,
      switchTo: (next, route = 'queue') => {
        setApiActiveScope({ tenantId: next.tenantId, companyId: next.companyId });
        nav(buildScopedPath(route, next));
      },
    }),
    [tenantId, companyId, tenant?.Name, company?.Name, status, nav],
  );

  if (status === 'pending')
    return (
      <div className="h-screen flex items-center justify-center bg-slate-100 text-slate-400 text-sm font-medium">
        Ачааллаж байна…
      </div>
    );
  if (status === 'forbidden') return null;

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
